import axios from 'axios';
import { ApiResponse, Task, UserTask, TaskProgress } from '../types';
import { API_CONFIG } from '../config/apiConfig';

export interface ClaimRewardResponse {
  userTask: UserTask;
  rewardPoints: number;
  rewardEnergy: number;
}

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 10000,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('kiloba_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('kiloba_refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          const { accessToken } = response.data.data;
          localStorage.setItem('kiloba_access_token', accessToken);
          
          // Retry the original request
          error.config.headers.Authorization = `Bearer ${accessToken}`;
          return api.request(error.config);
        } catch (refreshError) {
          localStorage.removeItem('kiloba_access_token');
          localStorage.removeItem('kiloba_refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const taskApi = {
  // Lấy danh sách tất cả tasks của user
  async getUserTasks(): Promise<ApiResponse<TaskProgress[]>> {
    const response = await api.get('/tasks');
    return response.data;
  },

  // Lấy tasks theo loại
  async getUserTasksByType(type: 'one_time' | 'time_limited' | 'daily'): Promise<ApiResponse<TaskProgress[]>> {
    const response = await api.get(`/tasks/type/${type}`);
    return response.data;
  },

  // Hoàn thành task một lần
  async completeOneTimeTask(taskId: number): Promise<ApiResponse<UserTask>> {
    const response = await api.post(`/tasks/${taskId}/complete`);
    return response.data;
  },

  // Hoàn thành daily task
  async completeDailyTask(category: string): Promise<ApiResponse<UserTask>> {
    const response = await api.post(`/tasks/daily/${category}/complete`);
    return response.data;
  },

  // Complete mini quiz task
  async completeMiniQuizTask(): Promise<ApiResponse<UserTask>> {
    const response = await api.post('/tasks/mini-quiz/complete');
    return response.data;
  },

  // Handle mini quiz result (thay thế completeMiniQuizTask và failMiniQuizTask)
  async handleMiniQuizResult(isCorrect: boolean): Promise<ApiResponse<UserTask>> {
    const response = await api.post('/tasks/mini-quiz/result', { isCorrect });
    return response.data;
  },

  // Start mini quiz task (lấy câu hỏi cho task)
  async startMiniQuizTask(): Promise<ApiResponse<{ userTask: UserTask; question: any }>> {
    const response = await api.post('/tasks/mini-quiz/start-task');
    return response.data;
  },

  // Answer mini quiz task question
  async answerMiniQuizTask(userTaskId: number, answer: boolean): Promise<ApiResponse<{ userTask: UserTask; isCorrect: boolean; isCompleted: boolean; explanation?: string }>> {
    const response = await api.post('/tasks/mini-quiz/answer-task', { userTaskId, answer });
    return response.data;
  },

  // Claim reward
  async claimReward(taskId: number): Promise<ApiResponse<ClaimRewardResponse>> {
    const response = await api.post(`/tasks/${taskId}/claim`);
    return response.data;
  },
};
